import React from "react";

import FlexContainer from "shared/components/FlexContainer";
import { SmallText } from "shared/components/Typography";
import {
  formatConversionDate,
  formatConversionRate,
  getInternationalFields,
  hasInternationalField,
  isForeignCurrency,
  isInternationalStream,
} from "shared/utils/employmentAndIncome";

type InternationalFields = ReturnType<typeof getInternationalFields>;

export const INTERNATIONAL_USD_TOOLTIP =
  "This document was uploaded as international but was identified as a US document.";

export const getDisplayCurrency = ({
  currency,
  originalCurrency,
}: InternationalFields) => originalCurrency ?? currency;

const RATE_DECIMAL_PLACES = 5;

const formatForeignAmount = (amountInCents: number) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInCents / 100);

const isUsableNumber = (value: any) =>
  hasInternationalField(value) && Number.isFinite(Number(value));

const isUsableRate = (value: any) => isUsableNumber(value) && Number(value) > 0;

const originalAmountSummary = (fields: InternationalFields) => {
  const { originalAmount, originalAnnualAmount } = fields;
  const originalToShow = isUsableNumber(originalAnnualAmount)
    ? originalAnnualAmount
    : originalAmount;

  if (!isUsableNumber(originalToShow)) return null;

  const amount = formatForeignAmount(Number(originalToShow));
  const currency = getDisplayCurrency(fields);

  return currency ? `${amount} ${currency}` : amount;
};

export const conversionAmountLine = (fields: InternationalFields) => {
  const amount = originalAmountSummary(fields);
  const rate = isUsableRate(fields.conversionRate)
    ? formatConversionRate(fields.conversionRate, RATE_DECIMAL_PLACES)
    : null;

  if (amount && rate) return `${amount} @ ${rate}`;
  if (amount) return amount;
  if (rate) return `${getDisplayCurrency(fields) ?? ""} @ ${rate}`.trim();
  return null;
};

export const conversionDateLine = (fields: InternationalFields) => {
  if (!hasInternationalField(fields.conversionDate)) return null;

  const formatted = formatConversionDate(fields.conversionDate);
  return formatted ? `Converted ${formatted}` : null;
};

export const shouldShowConversionDetails = (
  enabled: boolean,
  streamVerificationMethod: string,
  fields: InternationalFields
) =>
  enabled &&
  isInternationalStream(streamVerificationMethod, fields) &&
  isForeignCurrency(getDisplayCurrency(fields)) &&
  (conversionAmountLine(fields) !== null ||
    conversionDateLine(fields) !== null);

interface ConvertedAmountProps {
  fields: InternationalFields;
  children: React.ReactNode;
}

export const ConvertedAmount = ({ fields, children }: ConvertedAmountProps) => {
  const amountLine = conversionAmountLine(fields);
  const dateLine = conversionDateLine(fields);

  return (
    <FlexContainer flexDirection="column" alignItems="flex-start">
      {children}
      {amountLine && <SmallText light>{amountLine}</SmallText>}
      {dateLine && <SmallText light>{dateLine}</SmallText>}
    </FlexContainer>
  );
};
